import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useSmartBack } from '../../hooks/useSmartBack';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  ArrowLeft, Loader2, Mail, Phone, MapPin, Calendar, DollarSign, FileText,
  Archive, RotateCcw, Trash2, Edit3, Sparkles, Shield, AlertTriangle, Receipt, Activity, Send
} from 'lucide-react';
import { toast } from 'sonner';
import UserAvatar from '../../components/UserAvatar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_BADGE = {
  active:   'bg-emerald-50 text-emerald-700 border-emerald-200',
  inactive: 'bg-red-50 text-red-600 border-red-200',
  archived: 'bg-zinc-100 text-zinc-500 border-zinc-200',
};

const fmtMoney = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

// ============ Sub-components ============

const StatTile = ({ label, value, sub, testId }) => (
  <div className="bg-white border border-zinc-200 rounded-xl p-4 min-w-0" data-testid={testId}>
    <p className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500 break-words">{label}</p>
    <p className="text-2xl font-bold text-zinc-900 mt-1 break-words">{value}</p>
    {sub && <p className="text-[11px] text-zinc-400 mt-1 break-words">{sub}</p>}
  </div>
);

const InfoRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-2.5 text-sm py-1.5">
    {Icon && <Icon className="w-3.5 h-3.5 text-zinc-400 mt-1 flex-shrink-0" />}
    <span className="text-zinc-500 text-xs min-w-[100px] flex-shrink-0">{label}:</span>
    <span className="text-zinc-800 break-words">{value || '—'}</span>
  </div>
);

const SafeDeleteDialog = ({ open, user, onClose, headers, onDeleted, onArchive, onDeactivate }) => {
  const [safety, setSafety] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmText, setConfirmText] = useState('');

  useEffect(() => {
    if (!open || !user) return;
    setSafety(null);
    setConfirmText('');
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API_URL}/api/admin-users/${user.id}/delete-safety`, { headers });
        setSafety(r.data);
      } catch (e) { toast.error('Could not check safety'); onClose(); }
      finally { setLoading(false); }
    })();
  }, [open, user, headers, onClose]);

  const confirm = async () => {
    if (confirmText !== 'DELETE') { toast.error('Type DELETE exactly'); return; }
    setLoading(true);
    try {
      await axios.delete(`${API_URL}/api/admin-users/${user.id}/hard`, { headers });
      toast.success(`${user.full_name} permanently deleted`);
      onDeleted();
    } catch (e) { toast.error(e.response?.data?.detail?.message || 'Delete failed'); }
    finally { setLoading(false); }
  };

  if (!user) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="detail-delete-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="h-5 w-5" /> Delete {user.full_name}?
          </DialogTitle>
        </DialogHeader>
        {loading && !safety ? (
          <div className="py-6 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-[#F56F05]" /></div>
        ) : safety && !safety.can_hard_delete ? (
          <div className="space-y-3 py-2">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
              <p className="text-sm font-semibold text-amber-900">This profile is connected to business records and cannot be permanently deleted.</p>
              <p className="text-xs text-amber-700 mt-1">You can archive or deactivate this profile instead.</p>
            </div>
            <div className="text-xs text-zinc-500 space-y-1">
              <p className="font-semibold uppercase tracking-wider text-[10px]">Blockers:</p>
              {safety.blockers.map(b => (
                <p key={b}>• {b.replace(/_/g, ' ')}: <span className="font-semibold text-zinc-700">{safety.counts[b]}</span></p>
              ))}
            </div>
            <div className="flex gap-2 pt-1">
              <Button variant="outline" className="flex-1" onClick={() => { onDeactivate(); onClose(); }}>Deactivate</Button>
              <Button className="flex-1 bg-[#F56F05] hover:bg-[#e06505] text-white" onClick={() => { onArchive(); onClose(); }}>
                <Archive className="h-4 w-4 mr-2" /> Archive
              </Button>
            </div>
          </div>
        ) : safety ? (
          <div className="space-y-3 py-2">
            <p className="text-sm text-zinc-600">No business records found. This will permanently delete the profile.</p>
            <div>
              <Label className="text-xs text-zinc-500">Type <code className="bg-zinc-100 px-1.5 py-0.5 rounded text-red-600 font-bold">DELETE</code> to confirm</Label>
              <Input value={confirmText} onChange={e => setConfirmText(e.target.value)} placeholder="DELETE"
                className="mt-1 rounded-xl border-zinc-200 font-mono" autoFocus data-testid="detail-delete-input" />
            </div>
            <div className="flex gap-2 pt-1">
              <Button variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
              <Button className="flex-1 bg-red-600 hover:bg-red-700 text-white" onClick={confirm}
                disabled={loading || confirmText !== 'DELETE'} data-testid="detail-delete-confirm">
                {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                Permanently delete
              </Button>
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

// ============ Main page ============

export default function AdminUserDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { back } = useSmartBack('/admin/users');
  const { token, user: currentUser } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const isMaster = currentUser?.role === 'master_admin';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const fetchDetail = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin-users/${id}/detail`, { headers });
      setData(r.data);
    } catch (e) { toast.error('Could not load profile'); }
    finally { if (!silent) setLoading(false); }
  }, [id, headers]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  const changeStatus = useCallback(async (newStatus) => {
    toast.dismiss(); // clear any prior toast so it doesn't intercept clicks on action buttons in the sticky top-right header
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/api/admin-users/${id}/status`, { status: newStatus }, { headers });
      const verbs = { active: 'restored', inactive: 'deactivated', archived: 'archived' };
      const name = data?.user?.full_name || 'User';
      toast.success(`${name} ${verbs[newStatus] || 'updated'}`, { duration: 1500 });
      await fetchDetail({ silent: true });
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
    finally { setActionLoading(false); }
  }, [id, headers, data?.user?.full_name, fetchDetail]);

  const resendInvite = useCallback(async () => {
    toast.dismiss();
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/api/admin-users/${id}/resend-invite`, {}, { headers });
      toast.success(`Invite re-sent to ${data?.user?.email}`, { duration: 2000 });
      await fetchDetail({ silent: true });
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to resend invite'); }
    finally { setActionLoading(false); }
  }, [id, headers, data?.user?.email, fetchDetail]);

  if (loading || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <Loader2 className="h-8 w-8 animate-spin text-[#F56F05]" />
      </div>
    );
  }

  const u = data.user;
  const status = u.status || (u.is_active !== false ? 'active' : 'inactive');
  const isSelf = u.id === currentUser?.id;
  const isMasterAdmin = u.role === 'master_admin';
  const canModify = isMaster && !isSelf && !isMasterAdmin;
  const isClient = u.role === 'client';
  const isCleaner = u.role === 'cleaner';

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="admin-user-detail-page">
      {/* Header */}
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-3 flex-wrap">
          <Button variant="ghost" size="icon" onClick={back} data-testid="back-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <UserAvatar user={u} size="md" testId="detail-avatar" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl font-bold text-zinc-900 truncate">{u.full_name}</h1>
              <Badge className={`text-[10px] border font-semibold capitalize ${STATUS_BADGE[status]}`} data-testid="detail-status-badge">{status}</Badge>
              <Badge variant="outline" className="text-[10px] capitalize" data-testid="detail-role-badge">{u.role?.replace('_', ' ')}</Badge>
            </div>
            <p className="text-xs text-zinc-500 truncate mt-0.5">{u.email}</p>
          </div>
          {/* Action buttons (master_admin only, can't act on self or other master_admins) */}
          {canModify ? (
            <div className="flex flex-wrap gap-2">
              {status === 'archived' ? (
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  onClick={() => changeStatus('active')} disabled={actionLoading} data-testid="detail-restore-btn">
                  <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Restore
                </Button>
              ) : (
                <>
                  <Button variant="outline" size="sm"
                    onClick={() => changeStatus(status === 'active' ? 'inactive' : 'active')}
                    disabled={actionLoading} data-testid="detail-deactivate-btn">
                    {status === 'active' ? 'Deactivate' : 'Reactivate'}
                  </Button>
                  <Button variant="outline" size="sm"
                    onClick={() => changeStatus('archived')} disabled={actionLoading} data-testid="detail-archive-btn">
                    <Archive className="h-3.5 w-3.5 mr-1.5" /> Archive
                  </Button>
                  <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50"
                    onClick={() => setDeleteOpen(true)} data-testid="detail-delete-btn">
                    <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete
                  </Button>
                </>
              )}
            </div>
          ) : (isSelf || isMasterAdmin) && (
            <Badge variant="outline" className="text-xs text-zinc-500 gap-1.5"><Shield className="h-3 w-3" /> {isSelf ? 'You' : 'Master admin'} — protected</Badge>
          )}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        {/* Invite-pending banner — shown until the invitee logs in for the first time */}
        {u.invite_pending && canModify && (
          <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="invite-pending-banner">
            <Mail className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-amber-900">Invite sent — waiting on first sign-in</p>
              <p className="text-xs text-amber-700 mt-0.5">
                {u.full_name?.split(' ')[0] || 'They'} hasn&apos;t set their password yet. The original link is valid for 7 days.
              </p>
            </div>
            <Button
              size="sm"
              className="bg-[#F56F05] hover:bg-[#e06505] text-white shrink-0"
              onClick={resendInvite}
              disabled={actionLoading}
              data-testid="detail-resend-invite-btn"
            >
              <Send className="h-3.5 w-3.5 mr-1.5" /> Resend invite
            </Button>
          </div>
        )}

        {/* Stats grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="detail-stats-grid">
          <StatTile testId="stat-appointments" label="Appointments" value={data.stats.appointments_total}
            sub={`${data.stats.appointments_completed} completed`} />
          {isClient && (
            <>
              <StatTile testId="stat-invoices" label="Invoices" value={data.stats.invoices_total}
                sub={`${fmtMoney(data.stats.invoices_paid_cents)} paid`} />
              <StatTile testId="stat-outstanding" label="Outstanding" value={fmtMoney(data.stats.invoices_outstanding_cents)} />
              {data.referrals && (
                <StatTile testId="stat-referrals" label="Referrals" value={data.referrals.invited}
                  sub={`${data.referrals.qualified} earned`} />
              )}
            </>
          )}
          {isCleaner && (
            <>
              <StatTile testId="stat-payouts" label="Payouts" value={data.stats.payouts_total}
                sub={`${fmtMoney(data.stats.payouts_amount_cents)} total`} />
              <StatTile testId="stat-cancelled" label="Cancelled" value={data.stats.appointments_cancelled} />
            </>
          )}
        </div>

        {/* Tabs */}
        <Tabs defaultValue="overview" data-testid="detail-tabs">
          <TabsList className="bg-white border border-zinc-200 rounded-xl">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="appointments" data-testid="tab-appointments">Appointments</TabsTrigger>
            {isClient && <TabsTrigger value="invoices" data-testid="tab-invoices">Invoices</TabsTrigger>}
            {isCleaner && <TabsTrigger value="payouts" data-testid="tab-payouts">Payouts</TabsTrigger>}
            <TabsTrigger value="activity" data-testid="tab-activity">Activity</TabsTrigger>
          </TabsList>

          {/* OVERVIEW */}
          <TabsContent value="overview" className="space-y-4 mt-4">
            <Card className="border-zinc-200 shadow-sm">
              <CardContent className="p-5 space-y-1">
                <h3 className="text-sm font-bold text-zinc-700 uppercase tracking-wider mb-2">Contact</h3>
                <InfoRow icon={Mail} label="Email" value={u.email} />
                <InfoRow icon={Phone} label="Phone" value={u.phone} />
                <InfoRow icon={Calendar} label="Created" value={fmtDate(u.created_at)} />
              </CardContent>
            </Card>

            {data.profile && (
              <Card className="border-zinc-200 shadow-sm">
                <CardContent className="p-5 space-y-1">
                  <h3 className="text-sm font-bold text-zinc-700 uppercase tracking-wider mb-2">
                    {isClient ? 'Client Profile' : 'Cleaner Profile'}
                  </h3>
                  {isClient && (
                    <>
                      <InfoRow label="Lead source" value={data.profile.lead_source} />
                      <InfoRow label="Client type" value={data.profile.client_type} />
                      <InfoRow label="Frequency" value={data.profile.cleaning_frequency} />
                      <InfoRow label="Agreed price" value={data.profile.agreed_price ? `$${data.profile.agreed_price}` : '—'} />
                      <InfoRow label="Auto-pay" value={data.profile.auto_pay_enabled ? 'Enabled' : 'Disabled'} />
                      <InfoRow label="Stripe customer" value={data.profile.stripe_customer_id ? '✓ Linked' : '—'} />
                    </>
                  )}
                  {isCleaner && (
                    <>
                      <InfoRow label="Worker type" value={data.profile.worker_type} />
                      <InfoRow label="Pay structure" value={data.profile.pay_structure} />
                      <InfoRow label="Hourly rate" value={data.profile.hourly_rate ? `$${data.profile.hourly_rate}/hr` : '—'} />
                      <InfoRow label="Commission" value={data.profile.commission_rate ? `${data.profile.commission_rate}%` : '—'} />
                      <InfoRow label="Available" value={data.profile.is_available ? 'Yes' : 'No'} />
                    </>
                  )}
                  {data.profile.notes && <InfoRow label="Notes" value={data.profile.notes} />}
                </CardContent>
              </Card>
            )}

            {isClient && data.referrals && (
              <Card className="border-zinc-200 shadow-sm">
                <CardContent className="p-5 space-y-1">
                  <h3 className="text-sm font-bold text-zinc-700 uppercase tracking-wider mb-2 flex items-center gap-2">
                    <Sparkles className="h-3.5 w-3.5 text-[#F56F05]" /> Referrals
                  </h3>
                  <InfoRow label="Their code" value={data.referrals.code} />
                  <InfoRow label="Invited" value={`${data.referrals.invited} friend${data.referrals.invited === 1 ? '' : 's'}`} />
                  <InfoRow label="Qualified" value={`${data.referrals.qualified} earned`} />
                  <InfoRow label="Pending credits" value={`${data.referrals.pending_credits} × 10%-off`} />
                  {data.referrals.welcome_discount_pending && <InfoRow label="Welcome discount" value="Pending (1st clean)" />}
                </CardContent>
              </Card>
            )}

            {isClient && data.addresses?.length > 0 && (
              <Card className="border-zinc-200 shadow-sm">
                <CardContent className="p-5 space-y-2">
                  <h3 className="text-sm font-bold text-zinc-700 uppercase tracking-wider mb-2">Addresses</h3>
                  {data.addresses.map(a => (
                    <div key={a.id} className="flex items-start gap-2 text-sm">
                      <MapPin className="h-3.5 w-3.5 text-zinc-400 mt-1 flex-shrink-0" />
                      <p className="text-zinc-700">{a.line1}{a.line2 ? `, ${a.line2}` : ''}, {a.city}, {a.state} {a.zip_code}</p>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* APPOINTMENTS */}
          <TabsContent value="appointments" className="mt-4">
            <Card className="border-zinc-200 shadow-sm">
              <CardContent className="p-0 overflow-x-auto">
                {data.appointments.length === 0 ? (
                  <div className="p-8 text-center text-zinc-400 text-sm">No appointments</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-zinc-50 border-b border-zinc-100">
                      <tr>
                        <th className="text-left text-[10px] font-bold text-zinc-500 uppercase tracking-wider px-4 py-2.5">Date</th>
                        <th className="text-left text-[10px] font-bold text-zinc-500 uppercase tracking-wider px-4 py-2.5">Service</th>
                        <th className="text-left text-[10px] font-bold text-zinc-500 uppercase tracking-wider px-4 py-2.5 hidden md:table-cell">Address</th>
                        <th className="text-left text-[10px] font-bold text-zinc-500 uppercase tracking-wider px-4 py-2.5">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.appointments.map(a => (
                        <tr key={a.id} className="border-b border-zinc-50 hover:bg-zinc-50/50">
                          <td className="px-4 py-2.5 text-zinc-700">{fmtDate(a.start_time)}</td>
                          <td className="px-4 py-2.5 text-zinc-700">{a.service_type || '—'}</td>
                          <td className="px-4 py-2.5 text-zinc-500 hidden md:table-cell truncate max-w-[280px]">{a.property_address || '—'}</td>
                          <td className="px-4 py-2.5">
                            <Badge variant="outline" className="text-[10px] capitalize">{a.status}</Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* INVOICES (client) */}
          {isClient && (
            <TabsContent value="invoices" className="mt-4">
              <Card className="border-zinc-200 shadow-sm">
                <CardContent className="p-0 overflow-x-auto">
                  {data.invoices.length === 0 ? (
                    <div className="p-8 text-center text-zinc-400 text-sm">No invoices</div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead className="bg-zinc-50 border-b border-zinc-100">
                        <tr>
                          <th className="text-left text-[10px] font-bold text-zinc-500 uppercase tracking-wider px-4 py-2.5">Number</th>
                          <th className="text-left text-[10px] font-bold text-zinc-500 uppercase tracking-wider px-4 py-2.5">Issued</th>
                          <th className="text-right text-[10px] font-bold text-zinc-500 uppercase tracking-wider px-4 py-2.5">Total</th>
                          <th className="text-left text-[10px] font-bold text-zinc-500 uppercase tracking-wider px-4 py-2.5">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.invoices.map(i => (
                          <tr key={i.id} className="border-b border-zinc-50 hover:bg-zinc-50/50">
                            <td className="px-4 py-2.5 text-zinc-700 font-mono text-xs">
                              <Link to={`/admin/invoices`} className="hover:text-[#F56F05]">#{i.invoice_number}</Link>
                            </td>
                            <td className="px-4 py-2.5 text-zinc-500">{fmtDate(i.created_at)}</td>
                            <td className="px-4 py-2.5 text-right text-zinc-700 font-semibold">${(i.total || 0).toFixed(2)}</td>
                            <td className="px-4 py-2.5">
                              <Badge variant="outline" className="text-[10px] capitalize">{i.status}</Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* PAYOUTS (cleaner) */}
          {isCleaner && (
            <TabsContent value="payouts" className="mt-4">
              <Card className="border-zinc-200 shadow-sm">
                <CardContent className="p-0 overflow-x-auto">
                  {data.payouts.length === 0 ? (
                    <div className="p-8 text-center text-zinc-400 text-sm">No payouts</div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead className="bg-zinc-50 border-b border-zinc-100">
                        <tr>
                          <th className="text-left text-[10px] font-bold text-zinc-500 uppercase tracking-wider px-4 py-2.5">Week of</th>
                          <th className="text-right text-[10px] font-bold text-zinc-500 uppercase tracking-wider px-4 py-2.5">Gross</th>
                          <th className="text-right text-[10px] font-bold text-zinc-500 uppercase tracking-wider px-4 py-2.5">Net</th>
                          <th className="text-left text-[10px] font-bold text-zinc-500 uppercase tracking-wider px-4 py-2.5">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.payouts.map(p => (
                          <tr key={p.id} className="border-b border-zinc-50 hover:bg-zinc-50/50">
                            <td className="px-4 py-2.5 text-zinc-700">{fmtDate(p.week_starting)}</td>
                            <td className="px-4 py-2.5 text-right text-zinc-500">${(p.gross_amount || 0).toFixed(2)}</td>
                            <td className="px-4 py-2.5 text-right text-zinc-700 font-semibold">${(p.net_amount || 0).toFixed(2)}</td>
                            <td className="px-4 py-2.5">
                              <Badge variant="outline" className="text-[10px] capitalize">{p.status || 'pending'}</Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* ACTIVITY (audit log) */}
          <TabsContent value="activity" className="mt-4">
            <Card className="border-zinc-200 shadow-sm">
              <CardContent className="p-0">
                {data.audit.length === 0 ? (
                  <div className="p-8 text-center text-zinc-400 text-sm">No activity recorded</div>
                ) : (
                  <ul className="divide-y divide-zinc-100">
                    {data.audit.map(a => (
                      <li key={a.id} className="px-5 py-3 flex items-start gap-3 text-sm">
                        <Activity className="h-3.5 w-3.5 text-zinc-400 mt-1 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-zinc-700">
                            <span className="font-semibold">{a.actor_email || 'system'}</span>
                            <span className="text-zinc-500"> {a.action?.replace(/_/g, ' ')}</span>
                          </p>
                          <p className="text-[11px] text-zinc-400">{fmtDate(a.created_at)} {a.created_at ? new Date(a.created_at).toLocaleTimeString() : ''}</p>
                          {a.details?.previous_status && (
                            <p className="text-[11px] text-zinc-500">→ {a.details.previous_status} → {a.details.new_status || a.action?.split('.').pop()}</p>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      <SafeDeleteDialog
        open={deleteOpen}
        user={data.user}
        headers={headers}
        onClose={() => setDeleteOpen(false)}
        onDeleted={() => { setDeleteOpen(false); navigate('/admin/users'); }}
        onArchive={() => changeStatus('archived')}
        onDeactivate={() => changeStatus('inactive')}
      />
    </div>
  );
}
